import type { FactoryParcelAddressLookupItem } from "services/factoryParcelApi";

export const PARCEL_PHONE_MAX_DIGITS = 10;
export const PARCEL_PHONE_MIN_SEARCH_DIGITS = 3;

/** ช่องค้นหาหลัก — แก้เบอร์หลังค้นหาแล้ว */
export const SEARCH_RECIPIENT_AGAIN_MSG = "โปรดค้นหาที่อยู่ผู้รับ";

/** Modal สร้างที่อยู่ — เบอร์ไม่ตรงกับที่ใช้ค้นหา (บรรทัดที่ 1) */
export const PHONE_MISMATCH_SEARCH_LINE1 = "เบอร์โทรศัพท์ไม่ตรงกับการค้นหา";

/** Modal สร้างที่อยู่ — โปรดค้นหาใหม่ (บรรทัดที่ 2) */
export const PHONE_MISMATCH_SEARCH_LINE2 = "โปรดค้นหาที่อยู่ผู้รับอีกครั้ง";

export const PHONE_MISMATCH_SEARCH_MSG = `${PHONE_MISMATCH_SEARCH_LINE1} ${PHONE_MISMATCH_SEARCH_LINE2}`;

export function normalizePhoneDigits(value?: string): string {
  return String(value ?? "").replace(/\D/g, "");
}

export function sanitizeParcelPhone(value?: string): string {
  return normalizePhoneDigits(value).slice(0, PARCEL_PHONE_MAX_DIGITS);
}

/** แก้เบอร์ในช่องค้นหาหลังกดค้นหาแล้ว — ต้องค้นหาใหม่ */
export function isSearchPhoneStale(
  currentSearchInput: string,
  lastSearchedDigits: string
): boolean {
  if (!lastSearchedDigits) return false;
  return normalizePhoneDigits(currentSearchInput) !== lastSearchedDigits;
}

/**
 * เบอร์ใน Modal ไม่สอดคล้องกับที่ใช้ค้นหา (เช่น ค้นหา 081 แต่กรอก 089...)
 * — แสดงข้อความเตือนและล็อกฟิลด์อื่น / ปุ่มบันทึก
 */
export function isRecipientPhoneOutOfSearch(
  phone: string,
  lastSearchedDigits: string
): boolean {
  const entered = normalizePhoneDigits(phone);
  const searched = normalizePhoneDigits(lastSearchedDigits);
  if (!searched || !entered) return false;
  if (!entered.startsWith(searched)) return true;
  if (searched.length === 10) return entered !== searched;
  return false;
}

/** บันทึกที่อยู่ใหม่ได้เมื่อเบอร์ครบ 10 หลักและสอดคล้องกับผลค้นหา */
export function canSaveRecipientPhone(phone: string, lastSearchedDigits: string): boolean {
  const entered = normalizePhoneDigits(phone);
  const searched = normalizePhoneDigits(lastSearchedDigits);
  if (!searched || entered.length !== 10) return false;
  if (!entered.startsWith(searched)) return false;
  if (searched.length === 10) return entered === searched;
  return true;
}

export type ParcelPhoneSearchGroup = {
  telephone1: string;
  /** "ref_type|ref_id" — ใช้กรอง displayItems ตาม entity แทนการ filter ด้วย telephone1 */
  entity_key: string;
  displayName: string;
  previewAddress: string;
  addressCount: number;
  /** ISO string — ใช้เรียงกลุ่มล่าสุดก่อน */
  latestUpdatedAt: string;
  items: FactoryParcelAddressLookupItem[];
};

const toTime = (value?: string) => {
  if (!value) return 0;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : 0;
};

/** ที่อยู่จากสมุด standalone parcel_ship ของโรงงาน */
export function isFactoryParcelLookupSource(source?: string): boolean {
  return source === "factory_parcel";
}

/** เวลาใช้เรียง — ใช้ create_date เป็นหลัก */
export function getParcelAddressSortTime(item: FactoryParcelAddressLookupItem): number {
  return toTime(item.create_date ?? item.updated_at);
}

/**
 * เรียง card radio: 1) factory_parcel ก่อน 2) create_date ใหม่ก่อน
 */
export function compareParcelAddressLookupItems(
  a: FactoryParcelAddressLookupItem,
  b: FactoryParcelAddressLookupItem
): number {
  const aLast = Boolean(a.last_address);
  const bLast = Boolean(b.last_address);
  if (aLast !== bLast) return aLast ? -1 : 1;

  const aParcel = isFactoryParcelLookupSource(a.source);
  const bParcel = isFactoryParcelLookupSource(b.source);
  if (aParcel !== bParcel) return aParcel ? -1 : 1;
  const byCreate = getParcelAddressSortTime(b) - getParcelAddressSortTime(a);
  if (byCreate !== 0) return byCreate;
  return String(a.address_id).localeCompare(String(b.address_id));
}

export function sortParcelAddressLookupItems(
  items: FactoryParcelAddressLookupItem[]
): FactoryParcelAddressLookupItem[] {
  return [...items].sort(compareParcelAddressLookupItems);
}

/**
 * กำหนดการ์ดที่แสดง Tag「ที่อยู่จัดส่งล่าสุด」 — มีได้การ์ดเดียว
 * 1) last_address จาก API (ประวัติส่งพัสดุ standalone)
 * 2) ไม่มี → การ์ดแรกหลัง sort (factory_parcel + create_date ใหม่สุด)
 */
export function markParcelAddressLatestForDisplay(
  items: FactoryParcelAddressLookupItem[]
): FactoryParcelAddressLookupItem[] {
  if (items.length === 0) return items;
  const apiLastIndex = items.findIndex((i) => i.last_address);
  const targetIndex = apiLastIndex >= 0 ? apiLastIndex : 0;
  return items.map((item, index) => ({
    ...item,
    last_address: index === targetIndex,
  }));
}

const formatPreview = (item: FactoryParcelAddressLookupItem) => {
  const parts = [
    item.address?.trim(),
    item.county?.trim(),
    item.city?.trim(),
    item.state?.trim(),
    item.postcode?.trim(),
  ].filter(Boolean);
  return parts.join(" ");
};

/**
 * จัดกลุ่มที่อยู่ตาม entity (ref_type + ref_id) — 1 คน = 1 กลุ่ม
 * telephone1 ของกลุ่ม = searchedPhone (เบอร์ที่ใช้ค้นหา) เพื่อแสดงเบอร์ที่ตรงกับการค้นหา
 * แม้ที่อยู่ในกลุ่มจะมีเบอร์ต่างกัน ก็ยังนับรวมในกลุ่มเดียวกัน
 */
export function groupParcelAddressesByPhone(
  items: FactoryParcelAddressLookupItem[],
  searchedPhone?: string
): ParcelPhoneSearchGroup[] {
  const byEntity = new Map<string, FactoryParcelAddressLookupItem[]>();

  for (const item of items) {
    const key = `${item.ref_type}|${item.ref_id}`;
    const list = byEntity.get(key) ?? [];
    list.push(item);
    byEntity.set(key, list);
  }

  const groups: ParcelPhoneSearchGroup[] = [];

  for (const [entityKey, rawItems] of Array.from(byEntity.entries())) {
    const itemsSorted = sortParcelAddressLookupItems(rawItems);
    const newest = itemsSorted[0];
    const groupPhone = searchedPhone || String(newest.telephone1 ?? "").replace(/\D/g, "");
    groups.push({
      telephone1: groupPhone,
      entity_key: entityKey,
      displayName: newest.fullname?.trim() || "—",
      previewAddress: formatPreview(newest),
      addressCount: itemsSorted.length,
      latestUpdatedAt: newest.create_date ?? newest.updated_at ?? "",
      items: itemsSorted,
    });
  }

  groups.sort((a, b) => toTime(b.latestUpdatedAt) - toTime(a.latestUpdatedAt));
  return groups;
}
